// packages/components/src/support-ai/ExecutionPlanDisplay.tsx
import React from 'react';
import { ExecutionStep, type ExecutionStepData } from './ExecutionStep';
import { Button } from '@dopomogai/ui';
import { SlidersHorizontal, Database, FileCode2, Edit } from 'lucide-react';

export interface ExecutionPlan {
  tools: string[];
  dataSources: string[];
  steps: ExecutionStepData[];
}

interface ExecutionPlanDisplayProps {
  plan: ExecutionPlan;
  onApply: () => void;
}

const InfoChip: React.FC<{ icon: React.ReactNode, text: string }> = ({ icon, text }) => (
    <div className="flex items-center gap-1.5 bg-slate-100 border border-slate-300 text-slate-700 text-xs font-medium px-2 py-1 rounded-md">
        {icon}
        <span>{text}</span>
    </div>
);

export const ExecutionPlanDisplay: React.FC<ExecutionPlanDisplayProps> = ({ plan, onApply }) => {
  if (!plan) return null;
  const isEditable = plan.steps.every(step => step.status === 'pending');

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-md font-bold text-slate-800">
                <SlidersHorizontal size={20} />
                Execution Plan Details
            </h4>
            <Button 
                text="Edit" 
                variant="ghost" 
                size="sm" 
                icon={<Edit size={14}/>} 
                onClick={() => alert('Editing data sources is a planned feature.')}
            />
        </div>
        <div className="space-y-2 mt-3">
            <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-600">Tools:</p>
                {plan.tools.map(tool => <InfoChip key={tool} icon={<FileCode2 size={12} />} text={tool} />)}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-600">Data:</p>
                {plan.dataSources.map(source => <InfoChip key={source} icon={<Database size={12} />} text={source} />)}
            </div>
        </div>
      </div>

      <div className="space-y-3">
        {plan.steps.map(step => (
          <ExecutionStep key={step.id} step={step} />
        ))}
      </div>

      <div className="border-t-2 border-dashed border-slate-200 pt-4 flex items-center justify-end gap-3 flex-wrap">
         {isEditable && <Button text="Edit Steps" variant="secondary" onClick={() => alert('Editing steps is a planned feature.')} />}
         {isEditable && <Button text="Add Step" variant="secondary" onClick={() => alert('Adding a new step is a planned feature.')} />}
         <Button text="Generate New Plan" variant="outline" onClick={() => alert('AI-powered plan regeneration is a planned feature.')}/>
         <Button text="Apply Plan" variant="primary" onClick={onApply} />
      </div>
    </div>
  );
};